import { authService } from '@/services/auth.js';
import { backboneService } from '@/services/backbone.js';
import { toastService } from '@/services/toast.js';
import { profileClient } from '@/clients/profile-client.js';
import { entitlementsService } from '@/services/entitlements-service.js';
import { HatElement } from '@/components/hat-element.js';

class HatLayoutApp extends HatElement {
  private _unsubscribe: (() => void) | null = null;
  private _unsubscribeToast: (() => void) | null = null;
  private _profileName: string | null = null;
  private _docClickHandler: (() => void) | null = null;

  constructor() {
    super();

    this.root.innerHTML = `
      <style>
        :host {
          display: block;
          min-height: 100vh;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          position: relative;
        }

        :host::before {
          content: "";
          position: fixed;
          inset: 0;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          z-index: -1;
        }

        .wrapper {
          min-height: 100vh;
          display: flex;
          justify-content: center;
          padding: 20px;
        }

        .container {
          max-width: 1200px;
          width: 100%;
        }

        .header {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          color: white;
          padding: 12px 32px;
          z-index: 2000;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-sizing: border-box;
          background: linear-gradient(
            to bottom,
            rgba(0,0,0,0.25),
            rgba(0,0,0,0)
          );
          backdrop-filter: blur(3px);
          -webkit-backdrop-filter: blur(3px);
        }

        .header h1,
        .header span,
        .header button {
          text-shadow:
            0 2px 4px rgba(0, 0, 0, 0.7),
            0 0 10px rgba(0, 0, 0, 0.5);
        }

        .title {
          font-size: 1.6rem;
          font-weight: 700;
        }

        .right {
          display: flex;
          align-items: center;
          gap: 12px;
          position: relative;
        }

        .welcome {
          font-size: 0.95rem;
          opacity: 0.9;
        }

        .menu-btn {
          background: none;
          border: none;
          color: white;
          font-size: 22px;
          cursor: pointer;
        }

        .dropdown {
          position: absolute;
          right: 0;
          top: 36px;
          background: white;
          color: black;
          border-radius: 8px;
          box-shadow: 0 4px 14px rgba(0,0,0,0.2);
          display: none;
          flex-direction: column;
          min-width: 140px;
          overflow: hidden;
          
        }

        .dropdown button {
          padding: 10px;
          border: none;
          background: none;
          text-align: left;
          cursor: pointer;
          text-shadow: none;
        }

        .dropdown button:hover {
          background: #f2f2f2;
        }

        .hidden { display: none; }

        .menu.open .dropdown {
          display: flex;
        }

        main {
          margin-top: 80px;
        }

        .footer {
          text-align: center;
          color: white;
          opacity: 0.8;
          font-size: 0.9rem;
          padding: 10px 20px;
        }

        .toast-container {
          position: fixed;
          bottom: 24px;
          right: 24px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          z-index: 9999;
        }

        .toast {
          padding: 12px 18px;
          border-radius: 8px;
          color: white;
          font-size: 0.9rem;
          box-shadow: 0 4px 14px rgba(0,0,0,0.25);
          animation: toast-in 0.2s ease;
          max-width: 320px;
        }

        .toast.error   { background: #c0392b; }
        .toast.success { background: #27ae60; }
        .toast.info    { background: #2980b9; }

        @keyframes toast-in {
          from { transform: translateX(110%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      </style>

      <div class="wrapper">
        <div class="container">

          <header class="header">
            <div class="title"><span>Home Asset Tracker</span></div>

            <div class="right">
              <div class="welcome"></div>

              <div class="menu">
                <button class="menu-btn">☰</button>
                <div class="dropdown">
                  <button hat-entitlement="menu.admin" class="admin-action">Admin</button>
                  <button class="profile-action">Profile</button>
                  <button class="auth-action"></button>
                </div>
              </div>
            </div>
          </header>

          <main>
            <slot></slot>
          </main>

          <footer class="footer">
            <p>&copy; 2026 Home Asset Tracker</p>
          </footer>
        </div>
      </div>

      <div class="toast-container"></div>
    `;
  }

  connectedCallback() {
    super.connectedCallback();
    this._updateUI();
    if (authService.isAuthenticated()) {
      void this._loadProfileName();
      void entitlementsService.load();
    }

    // React to auth changes
    this._unsubscribe = authService.subscribe(() => {
      this._updateUI();
      if (authService.isAuthenticated()) {
        void this._loadProfileName();
        void entitlementsService.load();
      }
    });

    this._unsubscribeToast = toastService.subscribe(({ message, type }: { message: string; type: string }) => {
      this._showToast(message, type);
    });

    // Menu toggle
    const menu = this.root.querySelector('.menu')!;
    const btn = this.root.querySelector('.menu-btn')!;

    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      menu.classList.toggle('open');
    });

    this._docClickHandler = () => menu.classList.remove('open');
    document.addEventListener('click', this._docClickHandler);

    // Auth action
    this.root.querySelector('.auth-action')!
      .addEventListener('click', async () => {
        menu.classList.remove('open');
        if (authService.isAuthenticated()) {
          backboneService.navigateTo('/app/logout');
        } else {
          backboneService.navigateTo('/app/login');
        }
      });

    // Admin action
    this.root.querySelector('.admin-action')!
      .addEventListener('click', () => {
        menu.classList.remove('open');
        backboneService.navigateTo('/admin/invitations');
      });

    // Profile action
    this.root.querySelector('.profile-action')!
      .addEventListener('click', () => {
        menu.classList.remove('open');
        backboneService.navigateTo('/app/profile');
      });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._unsubscribe) this._unsubscribe();
    if (this._unsubscribeToast) this._unsubscribeToast();
    if (this._docClickHandler) document.removeEventListener('click', this._docClickHandler);
  }

  _showToast(message: string, type = 'error') {
    const container = this.root.querySelector('.toast-container')!;
    const toast = document.createElement('div');
    toast.className = `toast ${type}`;
    toast.textContent = message;
    container.appendChild(toast);
    setTimeout(() => toast.remove(), 4000);
  }

  async _loadProfileName() {
    try {
      const profile = await profileClient.getProfile();
      this._profileName = profile.userName;
      this._updateUI();
    } catch {
      // fall back to JWT name already shown
    }
  }

  _updateUI() {
    const welcome = this.root.querySelector('.welcome')!;
    const actionBtn = this.root.querySelector('.auth-action')!;

    if (authService.isAuthenticated()) {
      const name = this._profileName ?? authService.getUserName();
      welcome.innerHTML = `<span>Welcome: ${name ? `${name}` : '&lt;unknown&gt;'}</span>`;
      actionBtn.textContent = 'Logout';
    } else {
      welcome.textContent = '';
      actionBtn.textContent = 'Login';
    }

    entitlementsService.applyTo(this.root);
  }
}

customElements.define("hat-layout-app", HatLayoutApp);